// The client edge's frames (docs/08 "Frames"), as ocs-proto's `decodeFrame` hands them to
// JavaScript and `encodeFrame` accepts them: `ocs_proto::frames::ClientFrame` through
// serde-wasm-bindgen. Ids are 16-byte `Uint8Array`s (proto/ulid.ts converts), byte fields
// are `Uint8Array`, `u64`s are numbers, enum strings are the crate's serde names.
// vectors/frames.json pins every shape below against the Rust encoder.

export type IdBytes = Uint8Array;

export type EventType =
  | "prompt"
  | "assistant"
  | "tool_use"
  | "tool_result"
  | "question"
  | "answer"
  | "instruction"
  | "result"
  /** D82: a chat turn's partial output or thinking, coalesced by ocs-capture (S121). */
  | "progress";

export type Direction = "outbound" | "inbound";

export interface EventHeader {
  conversation_id: IdBytes;
  segment_id: IdBytes;
  seq: number;
  event_type: EventType;
  direction: Direction;
}

export interface EncryptedEvent {
  header: EventHeader;
  nonce: Uint8Array;
  ciphertext: Uint8Array;
  tag: Uint8Array;
}

export interface Cursor {
  segment_id: IdBytes;
  seq: number;
}

export interface WrappedKey {
  device_id: IdBytes;
  wrapped_cek: Uint8Array;
}

export interface SegmentOpen {
  conversation_id: IdBytes;
  segment_id: IdBytes;
  suite: string;
  nonce_prefix: Uint8Array;
  recipient_list_version: number;
  keys: WrappedKey[];
}

export interface EventFrame {
  event: EncryptedEvent;
  /** Plaintext metadata as JSON text (docs/07 metadata table). */
  meta: string;
}

export interface SegmentClose {
  segment_id: IdBytes;
  event_count: number;
}

export interface RunStatus {
  run_id: IdBytes;
  status: string;
  pending_questions: IdBytes[];
  worker_present: boolean;
}

/** A watched task, or a task of a watched group, changed (S97): metadata only. */
export interface TaskChanged {
  task_id: IdBytes;
  task_group_id: IdBytes;
  status: string;
  current_node?: string;
  updated_at_ms: number;
}

/** A watched group's own row changed (status, settings, poll outcome). */
export interface TaskGroupChanged {
  task_group_id: IdBytes;
  status: string;
  updated_at_ms: number;
}

export type SigAlg = "ed25519" | "ecdsa_p256_sha256";

export interface Signature {
  alg: SigAlg;
  bytes: Uint8Array;
}

export interface InboundMessage {
  message_id: IdBytes;
  device_id: IdBytes;
  event: EncryptedEvent;
  signature: Signature;
}

export type ConversationScope = "all" | { ids: IdBytes[] };

export interface Subscription {
  conversations?: ConversationScope;
  runs?: IdBytes[];
  terminal?: IdBytes;
  after?: Cursor[];
  /** Task groups whose group and task changes to receive (S97). */
  task_groups?: IdBytes[];
  /** Tasks whose changes to receive. */
  tasks?: IdBytes[];
}

export type ClientFrame =
  | { type: "hello"; session_token: string; client_version: string }
  | {
      type: "subscribe";
      conversations?: ConversationScope;
      runs: IdBytes[];
      terminal?: IdBytes;
      after: Cursor[];
      task_groups?: IdBytes[];
      tasks?: IdBytes[];
    }
  | { type: "unsubscribe"; conversations?: ConversationScope; runs: IdBytes[]; terminal?: IdBytes; task_groups?: IdBytes[]; tasks?: IdBytes[] }
  | ({ type: "segment.open" } & SegmentOpen)
  | ({ type: "event" } & EventFrame)
  | ({ type: "segment.close" } & SegmentClose)
  | ({ type: "run.status" } & RunStatus)
  | ({ type: "task.changed" } & TaskChanged)
  | ({ type: "task_group.changed" } & TaskGroupChanged)
  | ({ type: "send" } & InboundMessage)
  | { type: "sent"; message_id: IdBytes; delivered: boolean }
  | { type: "presence"; live: IdBytes[] }
  | {
      type: "terminal.open";
      session_id: IdBytes;
      /** Exactly one target: a workspace's interactive worker, or an environment's root shell (D79). */
      workspace_id?: IdBytes;
      environment_id?: IdBytes;
      cols: number;
      rows: number;
    }
  | { type: "terminal.data"; session_id: IdBytes; data: Uint8Array }
  | { type: "terminal.resize"; session_id: IdBytes; cols: number; rows: number }
  | { type: "terminal.close"; session_id: IdBytes; /** API → device: why it ended or was refused. */ reason?: string }
  | { type: "ping"; nonce: number }
  | { type: "pong"; nonce: number };

export type FrameType = ClientFrame["type"];

export type FrameOf<T extends FrameType> = Extract<ClientFrame, { type: T }>;

export const FRAME_TYPES: ReadonlySet<string> = new Set<FrameType>([
  "hello",
  "subscribe",
  "unsubscribe",
  "segment.open",
  "event",
  "segment.close",
  "run.status",
  "task.changed",
  "task_group.changed",
  "send",
  "sent",
  "presence",
  "terminal.open",
  "terminal.data",
  "terminal.resize",
  "terminal.close",
  "ping",
  "pong",
]);

/** The one suite this version implements (ocs_proto::suite::SUITE_V1). */
export const SUITE_V1 = "hpke-p256-hkdf-sha256-aes256gcm/1";
